import { useCallback, useEffect, useRef, useState } from 'react'
import { VisuallyHidden, useIsVisible } from '@overdose/components'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'
import type { Swiper as SwiperType } from 'swiper'
import { Controller, Navigation, Scrollbar, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/scrollbar'
import { ProductCard } from '~/components'
import { Button } from '~/components/Button'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { GTM, GTMEvent } from '~/lib'
import styles from './ProductCarousel.module.css'
import { ProductCarouselProps } from './ProductCarousel.types'

export function ProductCarousel({
  title,
  id,
  products = [],
  className,
}: ProductCarouselProps): React.JSX.Element {
  const [swiper, setSwiper] = useState<SwiperType | null>(null)

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [isViewed, setIsViewed] = useState(false)
  const ref = useRef()
  const isVisible = useIsVisible(ref)

  const handleNextAndPrevButtonsVisibility = useCallback(() => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }, [swiper])

  const handleSlidePrev = useCallback(() => {
    if (!swiper) {
      return
    }
    swiper.slidePrev()
    handleNextAndPrevButtonsVisibility()
  }, [swiper, handleNextAndPrevButtonsVisibility])

  const handleSlideNext = useCallback(() => {
    if (!swiper) {
      return
    }
    swiper.slideNext()
    handleNextAndPrevButtonsVisibility()
  }, [swiper, handleNextAndPrevButtonsVisibility])

  useEffect(() => {
    if (products.length > 0 && isVisible && !isViewed) {
      setIsViewed(true)
      GTM.dispatch(GTMEvent.VIEW_ITEM_LIST, {
        products,
        category: {
          name: title,
          id,
        },
      })
    }
  }, [id, isViewed, isVisible, products, title])

  const titleSection = title

  return (
    <div className={classNames('relative', styles.root, className)} ref={ref}>
      <div className='flex justify-between items-center mb-6 md:mb-10'>
        {title && (
          <Typography
            variant={TypographyVariant.Heading5}
            tag={TypographyTag.h2}
            className={styles.title}>
            {title}
          </Typography>
        )}
        <div className='hidden min-[1080px]:flex gap-1'>
          {(swiper?.allowSlideNext || swiper?.allowSlidePrev) && (
            <>
              <Button
                iconOnly
                icon={<IconChevronLeft />}
                onClick={handleSlidePrev}
                shape='circle'
                status='accent'
                disabled={isBeginning}
                style={{
                  '--btn-solid-color-bg-accent': 'var(--greyscale-200)',
                  '--btn-icon-size': '16px',
                  '--btn-size-md-height': '32px',
                  '--btn-size-md-width': '32px',
                }}>
                <VisuallyHidden>previous</VisuallyHidden>
              </Button>

              <Button
                iconOnly
                icon={<IconChevronRight />}
                onClick={handleSlideNext}
                shape='circle'
                status='accent'
                disabled={isEnd}
                style={{
                  '--btn-solid-color-bg-accent': 'var(--greyscale-200)',
                  '--btn-icon-size': '16px',
                  '--btn-size-md-height': '32px',
                  '--btn-size-md-width': '32px',
                }}>
                <VisuallyHidden>next</VisuallyHidden>
              </Button>
            </>
          )}
        </div>
      </div>
      <Swiper
        modules={[Controller, Navigation, Scrollbar, A11y]}
        controller={{ control: swiper }}
        onSwiper={(instance) => {
          setSwiper(instance)
        }}
        slidesPerView='auto'
        spaceBetween={16}
        scrollbar={{
          draggable: true,
          dragClass: styles.draggableElement,
        }}>
        {products.map(
          ({
            finalPrice,
            url,
            title,
            className,
            rating,
            ratingCount,
            regularPrice,
            sku,
            thumbnail,
            availableForDelivery,
            inStockText,
            inStock,
            inStore,
            isEndlessAisleFromSupplier,
          }) => {
            return (
              <SwiperSlide key={sku} className='pb-1 !h-auto'>
                <ProductCard
                  key={sku}
                  className={className}
                  finalPrice={finalPrice}
                  regularPrice={regularPrice}
                  title={title}
                  url={url}
                  showAddToCartButton
                  sku={sku}
                  rating={rating}
                  ratingCount={ratingCount}
                  thumbnail={thumbnail}
                  isFeatured={false}
                  titleSection={titleSection}
                  availableForDelivery={availableForDelivery}
                  inStockText={inStockText}
                  inStock={inStock}
                  inStore={inStore}
                  isEndlessAisleFromSupplier={isEndlessAisleFromSupplier}
                />
              </SwiperSlide>
            )
          }
        )}
      </Swiper>
    </div>
  )
}
